import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import DataApi from '../../api/DataApi';
import EmailApi from '../../api/EmailApi';
import jwt_decode from 'jwt-decode';
import LookupLayout from '../layout/LookupLayout';

const OBJECT_OPTIONS = [
  { value: 'lead', label: 'Leads' },
  { value: 'contact', label: 'Contacts' }
];

const DEVICE_WIDTHS = {
  mobile: 360,
  tablet: 768,
  desktop: null,
};
const TEMPLATE_BASE_WIDTH = 600;

/** Get user-facing error message from API error (same as EmailSender). */
const getApiErrorMessage = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

const EmailCampaignAudienceContentTab = ({
  recordData,
  objectName,
  recordId,
  initialAudience,
  onAudienceChange,
  onSaveDraft,
  onSendTest,
  onMarkReady,
  defaultTargetObject,
  defaultListViewName,
  selectedRecordIds = [],
  showSaveDraft = true
}) => {
  const [currentCampaignId, setCurrentCampaignId] = useState(recordId || null);
  const [selectedObject, setSelectedObject] = useState(initialAudience?.objectName || defaultTargetObject || '');
  const [selectedListView, setSelectedListView] = useState(initialAudience?.listViewName || defaultListViewName || '');
  const [listViews, setListViews] = useState([]);
  const [recipientCount, setRecipientCount] = useState(initialAudience?.recipientCount || 0);
  const [emailCount, setEmailCount] = useState(0);
  const [uniqueEmailCount, setUniqueEmailCount] = useState(0);
  const [selectedRecordCount, setSelectedRecordCount] = useState(0);
  const [selectedEmailCount, setSelectedEmailCount] = useState(0);
  const [selectedUniqueEmailCount, setSelectedUniqueEmailCount] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(initialAudience?.templateId || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [mailConfigs, setMailConfigs] = useState([]);
  const [selectedMailConfig, setSelectedMailConfig] = useState('');
  const [isLoadingMailConfigs, setIsLoadingMailConfigs] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [previewRecordId, setPreviewRecordId] = useState('');
  const [previewRecordLabel, setPreviewRecordLabel] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [localCampaignName, setLocalCampaignName] = useState('');

  const [subject, setSubject] = useState('');
  const [previewText] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [success, setSuccess] = useState('');
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [modalPreviewDevice, setModalPreviewDevice] = useState('desktop');
  const [previewScale, setPreviewScale] = useState(1);
  const [modalPreviewScale, setModalPreviewScale] = useState(1);

  const selectedRecordIdsSafe = useMemo(
    () => (Array.isArray(selectedRecordIds) ? selectedRecordIds.filter(Boolean) : []),
    [selectedRecordIds]
  );
  const selectedRecordKey = useMemo(
    () => selectedRecordIdsSafe.join(','),
    [selectedRecordIdsSafe]
  );
  const onAudienceChangeRef = useRef(onAudienceChange);
  const selectedTemplateRef = useRef(selectedTemplate);

  useEffect(() => {
    onAudienceChangeRef.current = onAudienceChange;
  }, [onAudienceChange]);

  useEffect(() => {
    selectedTemplateRef.current = selectedTemplate;
  }, [selectedTemplate]);
  const previewContainerRef = useRef(null);
  const modalPreviewContainerRef = useRef(null);


  const campaignName = useMemo(() => {
    const detailsFields = (recordData?.details || []).flatMap(section => section.fields || []);
    const field = detailsFields.find(item => item.fieldName?.toLowerCase() === 'name');
    return field?.value || recordData?.header?.titleField || '';
  }, [recordData]);

  useEffect(() => {
    if (campaignName && !localCampaignName) {
      setLocalCampaignName(campaignName);
    }
  }, [campaignName, localCampaignName]);

  const getFieldValueFromRecordData = useMemo(() => {
    return (fieldName) => {
      if (!recordData || !fieldName) return null;
      const normalized = String(fieldName).toLowerCase();

      const highlightFields = Array.isArray(recordData.highlightSection)
        ? recordData.highlightSection
        : [];
      const detailFields = (recordData?.details || []).flatMap(section => section.fields || []);
      const hiddenFields = recordData?.otherHiddenFields || [];
      const allFields = [...highlightFields, ...detailFields, ...hiddenFields];

      const match = allFields.find(field =>
        String(field.fieldName || field.field_name || '').toLowerCase() === normalized
      );
      return match?.value ?? null;
    };
  }, [recordData]);

  const formatDateTimeLocal = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    const pad = (num) => String(num).padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
  };

  const campaignStatus = useMemo(() => {
    const statusValue = getFieldValueFromRecordData('status');
    return statusValue ? String(statusValue).toLowerCase() : '';
  }, [getFieldValueFromRecordData]);

  const isCampaignLocked = !!currentCampaignId && campaignStatus && campaignStatus !== 'draft';
  const audienceSizeValue = useMemo(() => {
    const value = getFieldValueFromRecordData('audience_size');
    const normalized = value && typeof value === 'object'
      ? value.value || value.label || ''
      : value;
    return normalized ?? '';
  }, [getFieldValueFromRecordData]);

  useEffect(() => {
    if (scheduledAt) return;
    const scheduleValue = getFieldValueFromRecordData('schedule_time');
    if (scheduleValue) {
      const formatted = formatDateTimeLocal(scheduleValue);
      if (formatted) {
        setScheduledAt(formatted);
      }
    }
  }, [getFieldValueFromRecordData, scheduledAt]);

  useEffect(() => {
    if (!recordData) return;
    const targetObjectValue = getFieldValueFromRecordData('target_object');
    const listViewValue = getFieldValueFromRecordData('list_view_name');
    const templateValue = getFieldValueFromRecordData('email_template');
    const senderValue = getFieldValueFromRecordData('sender_email_address');
    const scheduledValue = getFieldValueFromRecordData('scheduled_date_time');

    if (!selectedObject && targetObjectValue) {
      setSelectedObject(targetObjectValue);
    }
    if (!selectedListView && listViewValue) {
      setSelectedListView(listViewValue);
    }
    if (!selectedTemplate && templateValue) {
      setSelectedTemplate(templateValue);
    }
    if (!selectedMailConfig && senderValue) {
      setSelectedMailConfig(senderValue);
    }
    if (!scheduledAt && scheduledValue) {
      const formatted = formatDateTimeLocal(scheduledValue);
      if (formatted) {
        setScheduledAt(formatted);
      }
    }
  }, [
    recordData,
    getFieldValueFromRecordData,
    selectedObject,
    selectedTemplate,
    selectedListView,
    selectedMailConfig,
    scheduledAt,
  ]);

  useEffect(() => {
    if (!currentCampaignId && recordId) {
      setCurrentCampaignId(recordId);
    }
  }, [recordId, currentCampaignId]);

  const ensureCampaignId = async () => {
    if (currentCampaignId) {
      return currentCampaignId;
    }

    if (!localCampaignName.trim()) {
      setErrorMessage('Campaign name is required.');
      return null;
    }

    if (!selectedObject || !selectedListView) {
      setErrorMessage('Please select target object and list view.');
      return null;
    }

    const dataApi = new DataApi('object/email_campaign');
    const payload = {
      name: localCampaignName.trim(),
      target_object: selectedObject,
      list_view_name: selectedListView,
      status: 'Draft',
    };
    const response = await dataApi.create(payload);
    const createdId =
      response?.data?.data?.id ||
      response?.data?.data?.recordId ||
      response?.data?.data?.data?.id ||
      response?.data?.data?.data?.recordId;
    if (!createdId) {
      setErrorMessage('Failed to create email campaign.');
      return null;
    }
    setCurrentCampaignId(createdId);
    return createdId;
  };

  const currentUserEmail = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwt_decode(token);
        return decoded?.email || '';
      }
    } catch (error) {
      console.error('Error decoding token for email:', error);
    }
    return '';
  }, []);

  useEffect(() => {
    if (!selectedObject) {
      setListViews([]);
      setSelectedListView('');
      setRecipientCount(0);
      return;
    }

    const fetchListViews = async () => {
      setIsLoading(true);
      setErrorMessage('');
      try {
        const listViewApi = new DataApi(`objectconfig/listviews/${selectedObject}`);
        const response = await listViewApi.fetchAll();
        if (response?.data?.success && response.data.data) {
          const fetchedListViews = response.data.data.map(view => ({
            value: view.name,
            label: view.label || view.name
          }));
          setListViews(fetchedListViews);
        } else {
          setListViews([]);
        }
      } catch (error) {
        console.error('Error fetching list views:', error);
        setErrorMessage('Unable to load list views.');
        setListViews([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchListViews();
  }, [selectedObject]);

  useEffect(() => {
    const fetchTemplates = async () => {
      setIsLoadingTemplates(true);
      setErrorMessage('');
      try {
        const emailApi = new EmailApi();
        const response = await emailApi.getEmailCampaignTemplates(selectedObject || null);
        console.log('getEmailCampaignTemplates response', response);
        if (response?.success) {
          setTemplates(response.data || []);
        } else {
          setTemplates([]);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        setTemplates([]);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [selectedObject]);

  useEffect(() => {
    const fetchMailConfigs = async () => {
      setIsLoadingMailConfigs(true);
      setErrorMessage('');
      try {
        const emailApi = new EmailApi();
        const response = await emailApi.getEmailConfigs();
        if (response?.success) {
          const allConfigs = response.data || [];
          // Only show configs that can send email (SMTP). Google/Outlook use OAuth and don't have SMTP password.
          const configs = allConfigs.filter((config) => {
            const t = String(config.type || '').toLowerCase();
            return t === 'smtp' || t === '';
          });
          setMailConfigs(configs);
          const normalizedUserEmail = String(currentUserEmail || '').toLowerCase();
          const matchedConfig = normalizedUserEmail
            ? configs.find(config => String(config.smtp_mail || '').toLowerCase() === normalizedUserEmail)
            : null;
          const defaultConfig = configs.find(config => config.is_default);
          const nextConfig = matchedConfig || defaultConfig || configs[0] || null;
          setSelectedMailConfig(nextConfig?.id || '');
        } else {
          setMailConfigs([]);
        }
      } catch (error) {
        console.error('Error fetching mail configs:', error);
        setMailConfigs([]);
      } finally {
        setIsLoadingMailConfigs(false);
      }
    };

    fetchMailConfigs();
  }, [currentUserEmail]);

  useEffect(() => {
    const calculateScale = () => {
      if (!previewContainerRef.current) return;
      if (previewDevice === 'desktop') {
        setPreviewScale(1);
        return;
      }
      const containerWidth = previewContainerRef.current.offsetWidth || 0;
      const deviceWidth = DEVICE_WIDTHS[previewDevice] || containerWidth;
      const targetWidth = Math.min(containerWidth, deviceWidth);
      if (!containerWidth || !targetWidth) {
        setPreviewScale(1);
        return;
      }
      setPreviewScale(Math.min(1, targetWidth / TEMPLATE_BASE_WIDTH));
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [previewDevice]);

  useEffect(() => {
    const calculateModalScale = () => {
      if (!modalPreviewContainerRef.current) return;
      if (modalPreviewDevice === 'desktop') {
        setModalPreviewScale(1);
        return;
      }
      const containerWidth = modalPreviewContainerRef.current.offsetWidth || 0;
      const deviceWidth = DEVICE_WIDTHS[modalPreviewDevice] || containerWidth;
      const targetWidth = Math.min(containerWidth, deviceWidth);
      if (!containerWidth || !targetWidth) {
        setModalPreviewScale(1);
        return;
      }
      setModalPreviewScale(Math.min(1, targetWidth / TEMPLATE_BASE_WIDTH));
    };

    calculateModalScale();
    window.addEventListener('resize', calculateModalScale);
    return () => window.removeEventListener('resize', calculateModalScale);
  }, [modalPreviewDevice, showPreviewModal]);

  useEffect(() => {
    if (!selectedTemplate) {
      return;
    }

    const template = templates.find(item => String(item.id) === String(selectedTemplate));
    if (template) {
      if (!previewRecordId) {
        setSubject(template.subject || '');
        setBodyHtml(template.body || '');
      }
    }
  }, [selectedTemplate, templates, previewRecordId]);

  useEffect(() => {
    if (!selectedTemplate || !previewRecordId || !selectedObject) {
      return;
    }

    const fetchParsedPreview = async () => {
      setIsLoading(true);
      setErrorMessage('');
      try {
        const emailApi = new EmailApi();
        const response = await emailApi.post('/test/parse-email-template', {
          templateId: selectedTemplate,
          recordId: previewRecordId,
          objectName: selectedObject,
        });
        if (response?.data?.success && response?.data?.result) {
          setSubject(response.data.result.subject || '');
          setBodyHtml(response.data.result.body || '');
        }
      } catch (error) {
        console.error('Error parsing email template preview:', error);
        setErrorMessage('Failed to render preview with merge fields.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchParsedPreview();
  }, [selectedTemplate, previewRecordId, selectedObject]);

  useEffect(() => {
    if (!selectedObject || !selectedListView) {
      return;
    }

    const fetchRecipientStats = async () => {
      setIsLoading(true);
      setErrorMessage('');
      try {
        const statsApi = new DataApi(
          `object/listview/${selectedObject}/${selectedListView}/stats`
        );
        const result = await statsApi.fetchAll('platform=web');
        if (result?.data?.success) {
          const stats = result.data.data || {};
          const total = stats.total ?? 0;
          setRecipientCount(total);
          setEmailCount(stats.emailCount ?? 0);
          setUniqueEmailCount(stats.uniqueEmailCount ?? 0);
          if (onAudienceChangeRef.current) {
            onAudienceChangeRef.current({
              objectName: selectedObject,
              listViewName: selectedListView,
              recipientCount: total,
              templateId: selectedTemplateRef.current
            });
          }
        } else {
          setRecipientCount(0);
          setEmailCount(0);
          setUniqueEmailCount(0);
          setErrorMessage(result?.data?.message || 'Unable to load recipients.');
        }

        if (selectedRecordKey) {
          const recordIdsParam = encodeURIComponent(selectedRecordKey);
          const selectedResult = await statsApi.fetchAll(
            `platform=web&recordIds=${recordIdsParam}`
          );
          if (selectedResult?.data?.success) {
            const selectedStats = selectedResult.data.data || {};
            setSelectedRecordCount(selectedStats.total ?? 0);
            setSelectedEmailCount(selectedStats.emailCount ?? 0);
            setSelectedUniqueEmailCount(selectedStats.uniqueEmailCount ?? 0);
          } else {
            setSelectedRecordCount(0);
            setSelectedEmailCount(0);
            setSelectedUniqueEmailCount(0);
          }
        } else {
          setSelectedRecordCount(0);
          setSelectedEmailCount(0);
          setSelectedUniqueEmailCount(0);
        }
      } catch (error) {
        console.error('Error fetching recipient count:', error);
        setRecipientCount(0);
        setEmailCount(0);
        setUniqueEmailCount(0);
        setSelectedRecordCount(0);
        setSelectedEmailCount(0);
        setSelectedUniqueEmailCount(0);
        setErrorMessage('Unable to load recipients.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecipientStats();
  }, [selectedObject, selectedListView, selectedRecordKey]);

  const handleSaveDraft = async () => {
    setSuccess('');
    setErrorMessage('');
    if (!localCampaignName.trim()) {
      setErrorMessage('Campaign name is required.');
      return;
    }
    if (onSaveDraft) {
      setIsBusy(true);
      try {
        await onSaveDraft({
          recordId,
          templateId: selectedTemplate,
          subject,
          previewText,
          bodyHtml
        });
        setSuccess('Draft saved successfully.');
      } catch (err) {
        setErrorMessage(getApiErrorMessage(err, 'Failed to save draft.'));
      } finally {
        setIsBusy(false);
      }
      return;
    }

    setIsBusy(true);
    try {
      const campaignId = await ensureCampaignId();
      if (!campaignId) {
        return;
      }
      const dataApi = new DataApi('object/email_campaign');
      const payload = {
        sender_email_address: selectedMailConfig || null,
        scheduled_date_time: scheduledAt || null,
        email_template: selectedTemplate || null,
        target_object: selectedObject || null,
        list_view_name: selectedListView || null,
      };
      const response = await dataApi.update(payload, campaignId);
      if (response?.data?.success) {
        setSuccess('Draft saved successfully.');
        if (campaignId) {
          window.location.href = `/view/email_campaign/${campaignId}`;
        }
      } else {
        setErrorMessage(response?.data?.message || 'Failed to save draft.');
      }
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Failed to save draft.'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleSendTest = async () => {
    setSuccess('');
    setErrorMessage('');

    if (!testEmail) {
      setErrorMessage('Please enter a test email address.');
      return;
    }

    if (onSendTest) {
      setIsBusy(true);
      try {
        await onSendTest({
          recordId,
          to: testEmail,
          subject,
          previewText,
          bodyHtml
        });
        setSuccess('Test email sent.');
      } catch (err) {
        setErrorMessage(getApiErrorMessage(err, 'Failed to send test email.'));
      } finally {
        setIsBusy(false);
      }
      return;
    }

    if (!selectedTemplate) {
      setErrorMessage('Please select an email template.');
      return;
    }

    if (!selectedMailConfig) {
      setErrorMessage('Please select a From Address.');
      return;
    }

    if (!previewRecordId) {
      setErrorMessage('Please select a Preview Record for merge fields.');
      return;
    }

    setIsBusy(true);
    try {
      const emailApi = new EmailApi();
      const response = await emailApi.sendEmail({
        to: testEmail,
        objectName: selectedObject || objectName,
        recordId: previewRecordId,
        selectedMailConfig,
        templateId: selectedTemplate,
      });
      if (response?.success) {
        setSuccess('Test email sent.');
      } else {
        setErrorMessage(response?.message || 'Failed to send test email.');
      }
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Failed to send test email.'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleStartCampaign = async () => {
    setSuccess('');
    setErrorMessage('');
    if (!localCampaignName.trim()) {
      setErrorMessage('Campaign name is required.');
      return;
    }

    if (!selectedObject || !selectedListView) {
      setErrorMessage('Please select target object and list view.');
      return;
    }
    if (!selectedTemplate) {
      setErrorMessage('Please select an email template.');
      return;
    }
    if (!selectedMailConfig) {
      setErrorMessage('Please select a From Address.');
      return;
    }

    setIsBusy(true);
    try {
      const campaignId = await ensureCampaignId();
      if (!campaignId) {
        return;
      }
      const dataApi = new DataApi('object/email_campaign');
      const payload = {
        sender_email_address: selectedMailConfig || null,
        scheduled_date_time: scheduledAt || null,
        email_template: selectedTemplate || null,
        target_object: selectedObject || null,
        list_view_name: selectedListView || null,
      };
      const updateResponse = await dataApi.update(payload, campaignId);
      if (!updateResponse?.data?.success) {
        setErrorMessage(updateResponse?.data?.message || 'Failed to save draft.');
        return;
      }

      const startApi = new DataApi('email_campaign');
      const startResponse = await startApi.post(`/${campaignId}/start`, {
        targetObject: selectedObject,
        listViewName: selectedListView,
        templateId: selectedTemplate,
        senderEmailAddress: selectedMailConfig,
        scheduledDateTime: scheduledAt || null,
        platform: 'web',
        recordIds: selectedRecordIdsSafe.length > 0 ? selectedRecordIdsSafe : undefined,
      });

      if (startResponse?.data?.success) {
        setSuccess('Campaign scheduled successfully.');
        if (campaignId) {
          window.location.href = `/view/email_campaign/${campaignId}`;
        }
      } else {
        setErrorMessage(startResponse?.data?.message || 'Failed to start campaign.');
      }
    } catch (err) {
      setErrorMessage(getApiErrorMessage(err, 'Failed to start campaign.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <>
      <div style={{ position: 'relative' }} aria-busy={isBusy}>
        {isBusy && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.7)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <Spinner animation="border" />
            <div className="text-muted">Saving…</div>
          </div>
        )}
        <Row className="g-3">
        <Col lg={6}>
          <Card className="h-100">
            <Card.Body>
        <Card.Title>Audience &amp; Content</Card.Title>
        <Form.Group className="mb-3">
          <Form.Label>Campaign Name</Form.Label>
              <Form.Control
            type="text"
            value={localCampaignName}
            onChange={(e) => setLocalCampaignName(e.target.value)}
            placeholder="Enter campaign name"
                required
          />
        </Form.Group>

              {isCampaignLocked && (
                <Alert variant="info">
                  This campaign is {campaignStatus}. Audience and actions are locked.
                </Alert>
              )}
              {errorMessage && (
          <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
            {errorMessage}
          </Alert>
        )}
        {success && (
          <Alert variant="success" onClose={() => setSuccess('')} dismissible>
            {success}
          </Alert>
        )}

            <Form.Group className="mb-3">
              <Form.Label>Select Target Object</Form.Label>
              <Form.Select
                value={selectedObject}
                onChange={(e) => {
                  setSelectedObject(e.target.value);
                  setSelectedListView('');
                  setRecipientCount(0);
                  setSelectedTemplate('');
                }}
                disabled={isLoading || isCampaignLocked}
              >
                <option value="">Select an object</option>
                {OBJECT_OPTIONS.map((obj) => (
                  <option key={obj.value} value={obj.value}>
                    {obj.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {selectedObject && (
              <Form.Group className="mb-3">
                <Form.Label>Select List View</Form.Label>
                <Form.Select
                  value={selectedListView}
                  onChange={(e) => setSelectedListView(e.target.value)}
                  disabled={isLoading || isCampaignLocked}
                >
                  <option value="">Select a list view</option>
                  {listViews.map((view) => (
                    <option key={view.value} value={view.value}>
                      {view.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Select Template</Form.Label>
              <Form.Select
                value={selectedTemplate}
                onChange={(e) => {
                  const newTemplate = e.target.value;
                  setSelectedTemplate(newTemplate);
                  if (onAudienceChange) {
                    onAudienceChange({
                      objectName: selectedObject,
                      listViewName: selectedListView,
                      recipientCount,
                      templateId: newTemplate
                    });
                  }
                }}
                disabled={isLoadingTemplates || isCampaignLocked}
                required
              >
                <option value="">Select a template</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name || template.subject || template.id}
                  </option>
                ))}
              </Form.Select>
              {isLoadingTemplates && (
                <div className="text-muted mt-2">
                  <Spinner size="sm" animation="border" className="me-2" />
                  Loading templates...
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>From Address</Form.Label>
              <Form.Select
                value={selectedMailConfig}
                onChange={(e) => setSelectedMailConfig(e.target.value)}
                disabled={isLoadingMailConfigs || isCampaignLocked}
              >
                <option value="">Select a sender</option>
                {mailConfigs.map(config => (
                  <option key={config.id} value={config.id}>
                    {config.name && config.smtp_mail
                      ? `${config.name} (${config.smtp_mail})`
                      : config.smtp_mail || config.name || config.id}
                  </option>
                ))}
              </Form.Select>
              {isLoadingMailConfigs && (
                <div className="text-muted mt-2">
                  <Spinner size="sm" animation="border" className="me-2" />
                  Loading senders...
                </div>
              )}
            </Form.Group>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Schedule Date/Time</Form.Label>
                    <Form.Control
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                      disabled={isCampaignLocked}
                  />
                  <Form.Text className="text-muted">
                    Leave empty to send immediately.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                {selectedObject && (
                  <Form.Group>
                    <Form.Label>Preview Record (for merge fields)</Form.Label>
                      <LookupLayout
                      key={`preview-record-${selectedObject}`}
                      objectName={selectedObject}
                      searchFields="name"
                      displayFields="name"
                      value={previewRecordId}
                      defaultLabel={previewRecordLabel}
                      onChange={(id, label) => {
                        setPreviewRecordId(id || '');
                        setPreviewRecordLabel(label || '');
                      }}
                        disabled={isLoading || isCampaignLocked}
                      isRequired={false}
                      className=""
                    />
                    <Form.Text className="text-muted">
                      Select a {selectedObject} to render merge fields in preview.
                    </Form.Text>
                  </Form.Group>
                )}
              </Col>
            </Row>

            {isLoading && (
              <div className="d-flex align-items-center gap-2 mb-2">
                <Spinner size="sm" animation="border" />
                <span>Loading…</span>
              </div>
            )}

            {isCampaignLocked ? (
              <div className="d-flex justify-content-between border-top pt-3 mb-4">
                <span><strong>Audience Size:</strong></span>
                <span>
                  <strong>{audienceSizeValue || '—'}</strong>
                </span>
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between border-top pt-3 mb-4">
                  <span><strong>Recipient Count:</strong></span>
                  <span><strong>{recipientCount.toLocaleString()}</strong></span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span><strong>Email Count:</strong></span>
                  <span><strong>{emailCount.toLocaleString()}</strong></span>
                </div>
                <div className="d-flex justify-content-between mb-4">
                  <span><strong>Unique Email Count:</strong></span>
                  <span><strong>{uniqueEmailCount.toLocaleString()}</strong></span>
                </div>
              </>
            )}

            {selectedRecordIdsSafe.length > 0 && (
              <>
                <div className="d-flex justify-content-between border-top pt-3 mb-2">
                  <span><strong>Selected Record Count:</strong></span>
                  <span><strong>{selectedRecordCount.toLocaleString()}</strong></span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span><strong>Selected Email Count:</strong></span>
                  <span><strong>{selectedEmailCount.toLocaleString()}</strong></span>
                </div>
                <div className="d-flex justify-content-between mb-4">
                  <span><strong>Selected Unique Email Count:</strong></span>
                  <span><strong>{selectedUniqueEmailCount.toLocaleString()}</strong></span>
                </div>
              </>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Test Email</Form.Label>
              <Form.Control
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="name@company.com"
                disabled={isCampaignLocked}
              />
            </Form.Group>

            <div className="d-flex flex-wrap gap-2">
              {showSaveDraft && !isCampaignLocked && (
                <Button
                  variant="outline-primary"
                  onClick={handleSaveDraft}
                  disabled={isBusy}
                >
                  Save Draft
                </Button>
              )}
              {!isCampaignLocked && (
                <Button
                  variant="outline-secondary"
                  onClick={handleSendTest}
                  disabled={isBusy}
                >
                  Send Test Email
                </Button>
              )}
              {!isCampaignLocked && (
                <Button
                  variant="success"
                  onClick={handleStartCampaign}
                  disabled={isBusy}
                >
                  Save & Run
                </Button>
              )}
            </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Email Preview</h6>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant={previewDevice === 'desktop' ? 'primary' : 'outline-primary'}
                    onClick={() => setPreviewDevice('desktop')}
                  >
                    Desktop
                  </Button>
                  <Button
                    size="sm"
                    variant={previewDevice === 'tablet' ? 'primary' : 'outline-primary'}
                    onClick={() => setPreviewDevice('tablet')}
                  >
                    iPad
                  </Button>
                  <Button
                    size="sm"
                    variant={previewDevice === 'mobile' ? 'primary' : 'outline-primary'}
                    onClick={() => setPreviewDevice('mobile')}
                  >
                    Mobile
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setShowPreviewModal(true)}
                  >
                    Full View
                  </Button>
                </div>
              </div>
              <div className="mb-2">
                <div className="text-muted small">Subject</div>
                <div className="fw-semibold">{subject || '—'}</div>
              </div>
              <div className="text-muted small mb-1">Body</div>
              <div
                className="d-flex justify-content-center"
                style={{ overflow: 'hidden' }}
                ref={previewContainerRef}
              >
                <div
                  className="border rounded p-2 bg-light"
                  style={{
                    minHeight: '240px',
                    overflowY: 'auto',
                    width: previewDevice === 'desktop' ? '100%' : `${TEMPLATE_BASE_WIDTH}px`,
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  <div
                    style={{
                      transform: `scale(${previewScale})`,
                      transformOrigin: 'top left',
                      width: previewDevice === 'desktop' ? '100%' : `${TEMPLATE_BASE_WIDTH}px`,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: bodyHtml || '<div style="color:#999;">No content yet.</div>'
                    }}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        </Row>
      </div>
      <Modal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Email Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-center gap-2 mb-3">
            <Button
              size="sm"
              variant={modalPreviewDevice === 'desktop' ? 'primary' : 'outline-primary'}
              onClick={() => setModalPreviewDevice('desktop')}
            >
              Desktop
            </Button>
            <Button
              size="sm"
              variant={modalPreviewDevice === 'tablet' ? 'primary' : 'outline-primary'}
              onClick={() => setModalPreviewDevice('tablet')}
            >
              iPad
            </Button>
            <Button
              size="sm"
              variant={modalPreviewDevice === 'mobile' ? 'primary' : 'outline-primary'}
              onClick={() => setModalPreviewDevice('mobile')}
            >
              Mobile
            </Button>
          </div>
          <div className="mb-3">
            <div className="text-muted small">Subject</div>
            <div className="fw-semibold">{subject || '—'}</div>
          </div>
          <div
            className="d-flex justify-content-center"
            ref={modalPreviewContainerRef}
            style={{ overflow: 'hidden' }}
          >
            <div
              className="border rounded p-3 bg-light"
              style={{
                minHeight: '400px',
                overflowY: 'auto',
                width: modalPreviewDevice === 'desktop'
                  ? '100%'
                  : `${TEMPLATE_BASE_WIDTH}px`,
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              <div
                style={{
                  transform: `scale(${modalPreviewScale})`,
                  transformOrigin: 'top left',
                  width: modalPreviewDevice === 'desktop'
                    ? '100%'
                    : `${TEMPLATE_BASE_WIDTH}px`,
                }}
                dangerouslySetInnerHTML={{
                  __html: bodyHtml || '<div style="color:#999;">No content yet.</div>'
                }}
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default EmailCampaignAudienceContentTab;
